// The Weekly Schedule's edit rules, as pure functions.
//
// scheduleOccurrences answers "when does a session happen?"; this module
// answers "what does an edit do?" — clashes, splits, the attendance windows a
// change must check, what a drop means. Nothing here reads React state or
// calls the API: whatever a rule needs is passed in, and it returns a
// decision for the component to carry out. That keeps every rule testable on
// its own (docs/Schedule-Test-Scenarios.md), and stops a rule from silently
// reading a stale copy of component state.

import { ScheduleSession } from './SessionDetailPanel';
import type { EditKind } from './ScopeChoiceModal';
import { occursOnDate, weekdayOf } from './scheduleOccurrences';
import { timeToMins, minsToTime, timeRangesOverlap, Slot } from './scheduleTime';
import { toDateString, addDays, daysBetween } from '../../services/dateUtils';

// Local mirror of the schedule_exception table. Two flavours:
//   - 'cancelled': hides the rule for that single date.
//   - 'modified':  overrides time / teachers / classrooms for that single date.
// The renderer (getEffectiveTimes) consults this list per-date so that the
// rule itself stays unchanged across other weeks.
// serverId is set once the POST /exceptions call returns and is used by
// Restore / future delete-by-id flows.
export interface LocalException {
  sessionId: string;       // local session id (not serverId)
  date: string;            // YYYY-MM-DD
  status: 'cancelled' | 'modified';
  serverId?: number;
  overrideStartTime?: string;
  overrideEndTime?: string;
  overrideTeacherIds?: number[];
  overrideLocationIds?: number[];
  reason?: string | null;  // free-text reason on 'cancelled' (Bank holiday, etc.)
}

// ── Clashes ──────────────────────────────────────────────────────────────
// Sessions are recurrence rules, so a clash isn't "same day + overlapping
// time" — it's "the two rules co-occur on some real date with overlapping
// times, minus any cancelled occurrences". We scan occurrence dates over a
// bounded horizon: weekly and fortnightly patterns repeat within 14 days, so a
// year is ample — a clash that doesn't surface inside it won't surface at all.
export const CONFLICT_HORIZON_DAYS = 366;

/** Everything a clash rule reads: the grid's sessions and exceptions. */
export interface ClashContext {
  sessions: ScheduleSession[];
  exceptions: LocalException[];
}

export interface SessionConflict {
  session: ScheduleSession;
  dates: string[]; // YYYY-MM-DD occurrences where the two overlap
}

export function isCancelledOnDate(ctx: ClashContext, sessionId: string, dateStr: string): boolean {
  return ctx.exceptions.some(
    e => e.sessionId === sessionId && e.date === dateStr && e.status === 'cancelled',
  );
}

// Effective minute-range for a session on a specific date, honouring any
// per-date 'modified' override (the renderer uses the same source of truth).
//
// `ignoreStoredOverride` is for hypothetical rules that don't own the stored
// exceptions filed under their id — chiefly the replacement rule a 'persist'
// edit is about to create. Those exceptions stay attached to the old rule,
// which is being end-dated, so applying them to the replacement would judge
// it against times it will never actually have.
export function effectiveTimeRangeOnDate(
  ctx: ClashContext,
  s: ScheduleSession,
  dateStr: string,
  ignoreStoredOverride = false,
): { start: number; end: number } {
  const ex = ignoreStoredOverride ? undefined : ctx.exceptions.find(
    e => e.sessionId === s.id && e.date === dateStr && e.status === 'modified',
  );
  if (ex && ex.overrideStartTime && ex.overrideEndTime) {
    return { start: timeToMins(ex.overrideStartTime), end: timeToMins(ex.overrideEndTime) };
  }
  return { start: timeToMins(s.startTime), end: timeToMins(s.endTime) };
}

// Dates within the bounded horizon where rules a and b both occur and their
// (effective) times overlap. Empty when they never clash.
export function coOccurringClashDates(
  ctx: ClashContext,
  a: ScheduleSession,
  b: ScheduleSession,
  ignoreStoredOverridesForA = false,
): string[] {
  const lo = a.startDate > b.startDate ? a.startDate : b.startDate;
  let hi = addDays(lo, CONFLICT_HORIZON_DAYS);
  if (a.endDate && a.endDate < hi) hi = a.endDate;
  if (b.endDate && b.endDate < hi) hi = b.endDate;
  if (hi < lo) return [];

  // Cheap reject: if the rule-level times don't overlap and neither side has
  // a per-date 'modified' override that could shift them, they can't clash.
  const rawOverlap = timeRangesOverlap(
    timeToMins(a.startTime), timeToMins(a.endTime),
    timeToMins(b.startTime), timeToMins(b.endTime),
  );
  const aHasMods = !ignoreStoredOverridesForA
    && ctx.exceptions.some(e => e.sessionId === a.id && e.status === 'modified');
  const bHasMods = ctx.exceptions.some(e => e.sessionId === b.id && e.status === 'modified');
  if (!rawOverlap && !aHasMods && !bHasMods) return [];

  const dates: string[] = [];
  const cursor = new Date(lo + 'T00:00');
  const hiTime = new Date(hi + 'T00:00').getTime();
  while (cursor.getTime() <= hiTime) {
    if (occursOnDate(a, cursor) && occursOnDate(b, cursor)) {
      const ds = toDateString(cursor);
      if (!isCancelledOnDate(ctx, a.id, ds) && !isCancelledOnDate(ctx, b.id, ds)) {
        const at = effectiveTimeRangeOnDate(ctx, a, ds, ignoreStoredOverridesForA);
        const bt = effectiveTimeRangeOnDate(ctx, b, ds);
        if (timeRangesOverlap(at.start, at.end, bt.start, bt.end)) {
          dates.push(ds);
          if (dates.length >= 8) break; // enough to report
        }
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

// Hard block: same child can't be double-booked.
export function findChildConflicts(
  ctx: ClashContext,
  candidate: ScheduleSession,
  excludeId: string,
  ignoreCandidateStoredOverrides = false,
): SessionConflict[] {
  if (candidate.childId == null) return [];
  const out: SessionConflict[] = [];
  for (const other of ctx.sessions) {
    if (other.id === excludeId || other.id === candidate.id) continue;
    if (other.childId == null || other.childId !== candidate.childId) continue;
    const dates = coOccurringClashDates(ctx, candidate, other, ignoreCandidateStoredOverrides);
    if (dates.length) out.push({ session: other, dates });
  }
  return out;
}

/**
 * Same-child clashes on ONE date, against an explicit candidate time range.
 *
 * findChildConflicts asks a rule-level question ("do these two rules ever
 * co-occur?"), and answers it by re-deriving the candidate's times per date
 * from the exceptions. That's the wrong question for an edit to a single
 * occurrence, and on a date that already carries a 'modified' override it
 * also gets the wrong answer: the stored override shadows the times the user
 * is actually proposing, so a genuine double-booking can pass unnoticed.
 *
 * Here the caller states the range outright, and we only look at the one
 * date — so nothing is re-derived and nothing is inferred about other dates.
 */
export function findChildConflictsOnDate(
  ctx: ClashContext,
  candidate: ScheduleSession,
  excludeId: string,
  dateStr: string,
  times: { startTime: string; endTime: string },
): SessionConflict[] {
  if (candidate.childId == null) return [];
  if (isCancelledOnDate(ctx, candidate.id, dateStr)) return [];
  const start = timeToMins(times.startTime);
  const end = timeToMins(times.endTime);
  const date = new Date(dateStr + 'T00:00');
  const out: SessionConflict[] = [];
  for (const other of ctx.sessions) {
    if (other.id === excludeId || other.id === candidate.id) continue;
    if (other.childId == null || other.childId !== candidate.childId) continue;
    if (!occursOnDate(other, date)) continue;
    if (isCancelledOnDate(ctx, other.id, dateStr)) continue;
    const ot = effectiveTimeRangeOnDate(ctx, other, dateStr);
    if (timeRangesOverlap(start, end, ot.start, ot.end)) {
      out.push({ session: other, dates: [dateStr] });
    }
  }
  return out;
}

// Soft warning: a teacher assigned to two overlapping sessions (different
// children). Same-child overlaps are reported by findChildConflicts instead.
export function findTeacherConflicts(
  ctx: ClashContext,
  candidate: ScheduleSession,
  excludeId: string,
): SessionConflict[] {
  if (candidate.teacherIds.length === 0) return [];
  const out: SessionConflict[] = [];
  for (const other of ctx.sessions) {
    if (other.id === excludeId || other.id === candidate.id) continue;
    if (other.childId != null && other.childId === candidate.childId) continue;
    if (!other.teacherIds.some(t => candidate.teacherIds.includes(t))) continue;
    const dates = coOccurringClashDates(ctx, candidate, other);
    if (dates.length) out.push({ session: other, dates });
  }
  return out;
}

// ── Edits ────────────────────────────────────────────────────────────────

// A session's start date is always its first occurrence, so its weekday IS
// the session's day: picking a start date on another weekday moves the bar
// to that day, whatever the frequency. Switching a rule to one-off pins it
// to the occurrence being viewed (not the rule's original start, which may
// be months back), and switching away un-pins it — a weekly rule whose
// end_date equals its start_date reads back as a one-off.
//
// `viewedOccurrenceDate` is the date `current` falls on in the week on screen.
export function alignToStartDate(
  current: ScheduleSession,
  requested: Partial<ScheduleSession>,
  viewedOccurrenceDate: string,
): Partial<ScheduleSession> {
  let updates = requested;
  if (updates.frequency === 'once' && current.frequency !== 'once') {
    updates = { ...updates, startDate: updates.startDate ?? viewedOccurrenceDate };
  } else if (updates.frequency !== undefined && updates.frequency !== 'once'
    && current.frequency === 'once') {
    updates = { ...updates, endDate: null };
  }
  const next = { ...current, ...updates };
  if (updates.startDate !== undefined) {
    updates = { ...updates, dayOfWeek: weekdayOf(next.startDate) };
  }
  if (next.frequency === 'once' && (updates.startDate !== undefined || updates.frequency !== undefined)) {
    updates = { ...updates, endDate: next.startDate };
  }
  return updates;
}

/** Where a "This date and future" save splits a rule. */
export interface SplitPlan {
  /** The replacement rule starts here. */
  fromDate: string;
  /** The old rule stops firing before this date. */
  splitAt: string;
  /** The old rule's new end_date — its last active day. */
  newEndDate: string;
  /** No occurrence before the split: mutate the rule in place instead. */
  inPlace: boolean;
}

export function planSplit(args: {
  kind: EditKind;
  snapshot: ScheduleSession;
  appliedUpdate: Partial<ScheduleSession> | 'delete';
  editDate: string | null;
  /** The date the snapshot falls on in the week on screen. */
  viewedDate: string;
}): SplitPlan {
  const { kind, snapshot, appliedUpdate, editDate, viewedDate } = args;
  // Where 'persist' splits the rule: the old one ends the day before, the
  // replacement starts here. Normally the occurrence being edited — but a
  // changed start date names the split itself (the start date is always the
  // first occurrence), so the rule changes from exactly the date picked.
  const changedStartDate = typeof appliedUpdate !== 'string' ? appliedUpdate.startDate : undefined;
  const fromDate = changedStartDate ?? editDate ?? viewedDate;
  // The old rule stops firing the day before the split. A day move splits
  // at whichever of its two dates comes first: dragging Wed 7 → Thu 8 has
  // to stop the Wednesday rule before Wed 7, not merely before Thu 8.
  const splitAt = kind === 'move-day' && editDate && editDate < fromDate ? editDate : fromDate;
  // The end_date is the *last active* date: the day before the split.
  const newEndDate = addDays(splitAt, -1);
  // When the rule has no occurrence before the split, newEndDate falls
  // before start_date — the DB CHECK rejects that — and there's no "past"
  // to preserve, so the change collapses into a single in-place mutation.
  const inPlace = newEndDate < snapshot.startDate;
  return { fromDate, splitAt, newEndDate, inPlace };
}

/** The attendance a change must be checked against: records for the rule
 *  from `from` (to `to`, if set), narrowed to those `affects` accepts. */
export interface AttendanceWindow {
  from: string;
  to?: string;
  affects?: (attendanceDate: string) => boolean;
}

// Attendance an in-place change to a rule would disturb. The records stay on
// the same rule, so only those that stop being a session of it count (a
// start date moved earlier on the same weekday disturbs nothing) — unless
// the times change, which alters every past session they record.
export function inPlaceAttendanceWindow(before: ScheduleSession, after: ScheduleSession): AttendanceWindow {
  const timesChange = after.startTime !== before.startTime || after.endTime !== before.endTime;
  return {
    from: after.startDate < before.startDate ? after.startDate : before.startDate,
    affects: timesChange ? undefined : d => !occursOnDate(after, new Date(d + 'T00:00')),
  };
}

// The attendance a scope-modal save must check. We refuse the operation if
// attendance recorded against this rule would be disturbed by it:
//   - one-off: only the date being edited.
//   - versioning (end-date + replacement): every date from the split on,
//     since those records would be left pointing at the ended rule.
//   - in-place update: see inPlaceAttendanceWindow.
export function attendanceWindowFor(args: {
  scope: 'one-off' | 'persist';
  plan: SplitPlan;
  snapshot: ScheduleSession;
  appliedUpdate: Partial<ScheduleSession> | 'delete';
  /** The date being edited (the occurrence in view). */
  occurrenceDate: string;
}): AttendanceWindow {
  const { scope, plan, snapshot, appliedUpdate, occurrenceDate } = args;
  if (scope === 'one-off') return { from: occurrenceDate, to: occurrenceDate };
  if (plan.inPlace && typeof appliedUpdate !== 'string') {
    return inPlaceAttendanceWindow(snapshot, { ...snapshot, ...appliedUpdate, startDate: plan.fromDate });
  }
  return { from: plan.splitAt };
}

// ── Drag-move ────────────────────────────────────────────────────────────

/** Where a dragged bar is being dropped. */
export interface MoveDrop {
  day: string;
  startTime: string;
  endTime: string;
}

/** What a drop means. The component carries it out. */
export type DropDecision =
  /** Dropped where it already was. */
  | { type: 'none' }
  /** The target date clashes for this child — refuse. */
  | { type: 'clash'; conflicts: SessionConflict[]; times: { startTime: string; endTime: string } }
  /** Never saved — an ordinary live edit. */
  | { type: 'fresh'; updates: Partial<ScheduleSession> }
  /** Same day, and the date already has its own time change — update that. */
  | { type: 'override'; override: LocalException; times: { startTime: string; endTime: string } }
  /** Everything else: a scope-modal edit (saved without asking for a one-off). */
  | {
    type: 'scope';
    kind: 'resize' | 'move-day';
    appliedUpdate: Partial<ScheduleSession>;
    editDate: string;
    persistOnly: boolean;
  };

// A dropped bar: same length, possibly a new time and/or day.
//   - never saved      → moves straight away (fresh-session edit)
//   - same day, the date already has a time change → that change is updated
//   - one-off          → moves in place, no scope question (one date only)
//   - repeating        → scope modal; "Just this date" for a new day is a
//                        cancellation plus a one-off
// `sourceDate` / `targetDate` are the dates the bar leaves and lands on in
// the week on screen.
export function decideDrop(
  ctx: ClashContext,
  target: ScheduleSession,
  drop: MoveDrop,
  sourceDate: string,
  targetDate: string,
): DropDecision {
  const sameDay = drop.day === target.dayOfWeek;
  // Where the bar actually sits: the rule, or the date's own time change.
  const origin = effectiveTimeRangeOnDate(ctx, target, sourceDate);
  if (sameDay && timeToMins(drop.startTime) === origin.start) return { type: 'none' };

  const times = { startTime: drop.startTime, endTime: drop.endTime };

  // The date it lands on takes the new times under every scope, so a clash
  // there refuses the move outright. Rule-wide clashes for "this date and
  // future" are judged later, once that scope is chosen.
  const conflicts = findChildConflictsOnDate(ctx, target, target.id, targetDate, times);
  if (conflicts.length > 0) return { type: 'clash', conflicts, times };

  // A start date is always the first session, so a new day brings its date.
  const dayChange: Partial<ScheduleSession> = sameDay
    ? {}
    : { dayOfWeek: drop.day, startDate: targetDate };

  if (target.serverId === undefined) return { type: 'fresh', updates: { ...dayChange, ...times } };

  const override = ctx.exceptions.find(
    e => e.sessionId === target.id && e.date === sourceDate && e.status === 'modified',
  );
  // The bar being dragged IS the date's time change — as resize does.
  if (sameDay && override) return { type: 'override', override, times };

  const isOneOff = target.frequency === 'once';
  // A moved rule keeps its number of sessions: an end date travels with the
  // move, by the same number of days (G-4). Without this, a Thursday rule
  // ending in five weeks moved to Friday lost its last session, and an ended
  // rule's last session moved later got a start after its end — which the
  // DB rejects, after the old rule had already been cut short.
  const endChange: Partial<ScheduleSession> = sameDay ? {}
    : isOneOff ? { endDate: targetDate }
      : target.endDate ? { endDate: addDays(target.endDate, daysBetween(sourceDate, targetDate)) }
        : {};
  return {
    type: 'scope',
    kind: sameDay ? 'resize' : 'move-day',
    appliedUpdate: {
      ...times,
      ...dayChange,
      ...endChange,
    },
    editDate: sourceDate,
    persistOnly: isOneOff,
  };
}

/** How far into a bar (minutes) it was grabbed, so it stays under the pointer. */
export function moveGrabOffset(grabbedSlot: Slot, barStartTime: string): number {
  return timeToMins(grabbedSlot.start) - timeToMins(barStartTime);
}

/**
 * Where a dragged bar lands with the pointer over slot `idx`: the grabbed
 * point kept under the pointer, the start snapped to the nearest slot, the
 * whole bar kept on the grid, the duration unchanged.
 */
export function moveTimesFor(args: {
  slots: Slot[];
  idx: number;
  grabOffset: number;
  startTime: string;
  endTime: string;
}): { startTime: string; endTime: string } {
  const { slots, idx, grabOffset, startTime, endTime } = args;
  const duration = timeToMins(endTime) - timeToMins(startTime);
  const firstMins = timeToMins(slots[0].start);
  const lastMins = timeToMins(slots[slots.length - 1].end);
  const wanted = timeToMins(slots[idx].start) - grabOffset;
  let snapped = firstMins;
  for (const sl of slots) {
    const m = timeToMins(sl.start);
    if (Math.abs(m - wanted) < Math.abs(snapped - wanted)) snapped = m;
  }
  snapped = Math.max(firstMins, Math.min(snapped, lastMins - duration));
  return { startTime: minsToTime(snapped), endTime: minsToTime(snapped + duration) };
}
